import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { INPUT_PRIORITY, modalInputRouter } from '../input/modalInputRouter'
import {
  requestWorldTeleport,
  worldDestinationForRoom,
  WORLD_DESTINATIONS,
} from '../pc/world/navigation'
import {
  useArenaStore,
  type WorkplacePhoneDestination,
  type WorkplacePhoneTab,
} from '../store'

const TABS: readonly { id: WorkplacePhoneTab; label: string }[] = [
  { id: 'places', label: 'Places' },
  { id: 'agents', label: 'Agents' },
  { id: 'work', label: 'Work' },
  { id: 'people', label: 'People' },
  { id: 'systems', label: 'Systems' },
]

function isEditableTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && (
    target.matches('input, textarea, select, button') || target.isContentEditable
  )
}

function runStatusColor(status: string): string {
  if (status === 'completed') return '#5eead4'
  if (status === 'failed' || status === 'cancelled') return '#fb7185'
  if (status === 'running') return '#8b9cff'
  return '#94a3b8'
}

function EmptyState({ children }: { children: string }) {
  return <div style={styles.empty}>{children}</div>
}

function ProjectionState({ status, world = false }: { status: string; world?: boolean }) {
  const label = status === 'loading' || status === 'connecting'
    ? `Connecting to ${world ? 'world presence' : 'workplace data'}…`
    : status === 'stale' || status === 'degraded'
      ? `${world ? 'World presence' : 'Workplace data'} is reconnecting. Last-known activity is not presented as live.`
      : `${world ? 'World presence' : 'Workplace data'} is offline.`
  return <div role="status" style={styles.projectionState}>{label}</div>
}

function SystemMetric({
  label,
  value,
  healthy,
}: {
  label: string
  value: string
  healthy?: boolean
}) {
  return (
    <div style={styles.systemMetric}>
      <span style={styles.systemLabel}>{label}</span>
      <strong style={{ ...styles.systemValue, ...(healthy === false ? styles.systemWarning : {}) }}>{value}</strong>
    </div>
  )
}

export function WorkplacePhone() {
  const open = useArenaStore((state) => state.isWorkplacePhoneOpen)
  const isPlaying = useArenaStore((state) => state.isPlaying)
  const isPaused = useArenaStore((state) => state.isPaused)
  const setOpen = useArenaStore((state) => state.setIsWorkplacePhoneOpen)
  const phoneRequest = useArenaStore((state) => state.workplacePhoneRequest)
  const consumePhoneRequest = useArenaStore((state) => state.consumeWorkplacePhoneRequest)
  const agents = useArenaStore((state) => state.agents)
  const chats = useArenaStore((state) => state.chats)
  const runs = useArenaStore((state) => state.runs)
  const tasks = useArenaStore((state) => state.tasks)
  const interactionsByRun = useArenaStore((state) => state.interactionsByRun)
  const subjectLaunches = useArenaStore((state) => state.subjectLaunchesByTaskId)
  const members = useArenaStore((state) => state.activeWorldMembers)
  const currentRoomId = useArenaStore((state) => state.currentWorldRoomId)
  const remoteStatus = useArenaStore((state) => state.remoteProjectionStatus)
  const worldStateStatus = useArenaStore((state) => state.worldStateStatus)
  const presenceStatus = useArenaStore((state) => state.presenceStatus)
  const health = useArenaStore((state) => state.health)
  const requestAgentTalk = useArenaStore((state) => state.requestAgentTalk)
  const openInspector = useArenaStore((state) => state.openInspector)
  const setActiveInteraction = useArenaStore((state) => state.setActiveInteraction)
  const launchTaskRun = useArenaStore((state) => state.launchTaskRun)
  const completionNotices = useArenaStore((state) => state.toasts.length)
  const [tab, setTab] = useState<WorkplacePhoneTab>('places')
  const [notice, setNotice] = useState('')
  const [search, setSearch] = useState('')
  const [lastSeenChatAt, setLastSeenChatAt] = useState(() => Date.now())
  const [requestedDestination, setRequestedDestination] = useState<WorkplacePhoneDestination | null>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const liveWorkRef = useRef<HTMLDivElement>(null)
  const launchableTasksRef = useRef<HTMLDivElement>(null)

  const pendingInteractions = useMemo(
    () => Object.values(interactionsByRun).flat().filter((interaction) => !interaction.respondedAt),
    [interactionsByRun],
  )
  const blockedTasks = tasks.filter((task) => task.status === 'blocked').length
  const unreadChats = chats.filter((chat) => {
    const value = chat.lastMessageAt ?? chat.updatedAt
    return value ? Date.parse(value) > lastSeenChatAt : false
  }).length
  const tabBadges: Partial<Record<WorkplacePhoneTab, number>> = {
    agents: unreadChats,
    work: pendingInteractions.length + blockedTasks + completionNotices,
    people: members.length,
  }

  function canOpen(): boolean {
    const state = useArenaStore.getState()
    return state.isPlaying && !state.isPaused && !state.isAgentTalkOpen &&
      state.activeInteraction === null && state.inspectorRunId === null
  }

  function openPhone(): void {
    if (!canOpen()) return
    // Claim modal ownership before pointer lock exits so ClickToPlay does not
    // misclassify this deliberate handoff as a pause.
    setOpen(true)
    setNotice('')
    document.exitPointerLock?.()
  }

  function closePhone(resume = true): void {
    setOpen(false)
    setNotice('')
    if (!resume) return
    const canvas = document.querySelector('canvas')
    if (canvas instanceof HTMLCanvasElement) {
      canvas.focus()
      canvas.requestPointerLock?.()
    }
  }

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): boolean => {
      if (event.code === 'Escape' && useArenaStore.getState().isWorkplacePhoneOpen) {
        closePhone()
        return true
      }
      if (event.code !== 'KeyP' || isEditableTarget(event.target)) return false
      if (useArenaStore.getState().isWorkplacePhoneOpen) closePhone()
      else openPhone()
      return true
    }
    return modalInputRouter.register(
      open ? INPUT_PRIORITY.interaction : INPUT_PRIORITY.room,
      onKeyDown,
    )
  }, [open, setOpen])

  useEffect(() => {
    // A contextual room request owns initial focus and scrolls to its work
    // destination below. Only generic launcher/P opens focus the phone shell.
    if (!open || phoneRequest || requestedDestination) return
    queueMicrotask(() => panelRef.current?.focus())
    // Deliberately keyed only to the open transition: consuming a contextual
    // one-shot request must not later steal focus back from its destination.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useLayoutEffect(() => {
    if (!phoneRequest) return
    setTab(phoneRequest.tab)
    setRequestedDestination(phoneRequest.destination ?? null)
    setNotice('')
    consumePhoneRequest(phoneRequest.nonce)
  }, [phoneRequest, consumePhoneRequest])

  useEffect(() => {
    if (!open || tab !== 'work' || !requestedDestination) return
    const target = requestedDestination === 'live-work'
      ? liveWorkRef.current
      : launchableTasksRef.current
    if (!target) return
    target.scrollIntoView?.({ block: 'start' })
    target.focus({ preventScroll: true })
    setRequestedDestination(null)
  }, [open, tab, requestedDestination])

  useEffect(() => {
    if (open && tab === 'agents') setLastSeenChatAt(Date.now())
  }, [open, tab])

  function teleport(destinationId: string): void {
    if (!requestWorldTeleport(destinationId)) {
      setNotice('The world is still connecting. Try again in a moment.')
      return
    }
    closePhone()
  }

  function openAgent(agentId: string): void {
    closePhone(false)
    requestAgentTalk(agentId)
  }

  function inspectRun(runId: string): void {
    closePhone(false)
    openInspector(runId)
  }

  function answerInteraction(interaction: (typeof pendingInteractions)[number]): void {
    closePhone(false)
    setActiveInteraction(interaction)
  }

  async function launchTask(taskId: string): Promise<void> {
    setNotice('Requesting an authorized launch…')
    const result = await launchTaskRun(taskId)
    if (result.status === 'accepted') {
      setNotice(result.observed
        ? 'Work launched and confirmed in the live run projection.'
        : 'Work accepted. Waiting for the canonical run projection…')
    } else {
      setNotice(result.reason === 'not-authorized'
        ? 'This task is no longer authorized for launch.'
        : result.reason === 'conflict'
          ? 'This task already has a launch being reconciled.'
          : 'Launch is unavailable right now. No success was assumed.')
    }
  }

  function joinMemberRoom(roomId: string): void {
    const destination = worldDestinationForRoom(roomId)
    if (!destination || !requestWorldTeleport(destination.id)) {
      setNotice('That teammate is in a room without an authorized public arrival point.')
      return
    }
    closePhone()
  }

  const normalizedSearch = search.trim().toLowerCase()
  const filteredDestinations = WORLD_DESTINATIONS.filter((destination) =>
    !normalizedSearch || `${destination.label} ${destination.description}`.toLowerCase().includes(normalizedSearch))
  const currentDestination = worldDestinationForRoom(currentRoomId)
  const sortedAgents = [...agents].sort((a, b) => a.name.localeCompare(b.name))
  const visibleRuns = [...runs]
    .sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt))
    .slice(0, 12)
  const visibleTasks = [...tasks]
    .sort((a, b) => Number(b.status === 'blocked') - Number(a.status === 'blocked') || a.title.localeCompare(b.title))
    .slice(0, 24)

  const visible = isPlaying || open
  if (!visible || isPaused) return null

  return (
    <div style={styles.root} data-testid="workplace-phone">
      {open && (
        <div
          ref={panelRef}
          style={styles.phone}
          role="dialog"
          aria-modal="true"
          aria-label="Workplace phone"
          tabIndex={-1}
        >
          <div style={styles.speaker} aria-hidden="true" />
          <div style={styles.topline}>
            <span>ANIMUS</span>
            <span style={presenceStatus === 'ready' ? styles.online : styles.reconnecting}>
              ● {presenceStatus === 'ready'
                ? 'WORLD ONLINE'
                : presenceStatus === 'disconnected'
                  ? 'WORLD OFFLINE'
                  : 'WORLD RECONNECTING'}
            </span>
          </div>
          <div style={styles.addressBar}>
            <span style={styles.lock}>◇</span>
            arena://{tab}
          </div>
          <nav style={styles.tabs} aria-label="Phone applications">
            {TABS.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => { setTab(item.id); setNotice('') }}
                style={{ ...styles.tab, ...(tab === item.id ? styles.activeTab : {}) }}
                aria-current={tab === item.id ? 'page' : undefined}
              >
                <span>{item.label}</span>
                {Boolean(tabBadges[item.id]) && (
                  <span style={styles.badge} aria-label={`${tabBadges[item.id]} notifications`}>
                    {tabBadges[item.id]! > 99 ? '99+' : tabBadges[item.id]}
                  </span>
                )}
              </button>
            ))}
          </nav>

          <div style={styles.viewport}>
            {tab === 'places' ? (
              <>
                <div style={styles.sectionTitle}>WHERE DO YOU WANT TO WORK?</div>
                <div style={styles.currentLocation}>
                  <span style={styles.liveDot}>●</span>
                  CURRENT · {currentDestination?.label ?? currentRoomId.toUpperCase()}
                </div>
                <input
                  aria-label="Search places"
                  placeholder="Search places…"
                  value={search}
                  onChange={(event) => setSearch(event.currentTarget.value)}
                  style={styles.search}
                />
                <div style={styles.places}>
                  {filteredDestinations.map((destination) => (
                    <button
                      key={destination.id}
                      type="button"
                      style={styles.place}
                      onClick={() => teleport(destination.id)}
                    >
                      <span style={styles.placeIcon}>{destination.plotId ? '⌂' : '⌖'}</span>
                      <span style={styles.placeCopy}>
                        <strong style={styles.placeLabel}>{destination.label}</strong>
                        <span style={styles.placeDescription}>{destination.description}</span>
                      </span>
                      <span style={styles.arrow}>→</span>
                    </button>
                  ))}
                  {filteredDestinations.length === 0 && <EmptyState>No matching places.</EmptyState>}
                </div>
              </>
            ) : tab === 'agents' ? (
              <>
                <div style={styles.sectionTitle}>AUTHORIZED AGENTS · {agents.length}</div>
                {remoteStatus !== 'fresh' && <ProjectionState status={remoteStatus} />}
                <div style={styles.list}>
                  {sortedAgents.map((agent) => {
                    const run = runs.find((candidate) => candidate.agentId === agent.id && candidate.status === 'running' && !candidate.stale)
                    const boundChats = chats.filter((chat) => chat.agentId === agent.id)
                    return (
                      <button key={agent.id} type="button" style={styles.card} onClick={() => openAgent(agent.id)}>
                        <span style={styles.avatar}>◎</span>
                        <span style={styles.cardCopy}>
                          <strong style={styles.cardTitle}>{agent.name}</strong>
                          <span style={styles.cardMeta}>{run ? `WORKING · ${run.title}` : 'ON STANDBY · HQ'}</span>
                          <span style={styles.cardDetail}>
                            {boundChats.length > 0 ? `${boundChats.length} conversation${boundChats.length === 1 ? '' : 's'} · Resume chat` : agent.canStartChat ? 'Start a private chat' : 'Inspect authorized profile'}
                          </span>
                        </span>
                        <span style={styles.arrow}>→</span>
                      </button>
                    )
                  })}
                  {agents.length === 0 && <EmptyState>No authorized agents are projected for this project.</EmptyState>}
                </div>
              </>
            ) : tab === 'work' ? (
              <>
                <div ref={liveWorkRef} data-testid="phone-live-work" tabIndex={-1} style={styles.sectionTitle}>
                  LIVE WORK · {runs.length} RUNS
                </div>
                <div style={styles.sectionTitle}>NEEDS ATTENTION · {pendingInteractions.length + blockedTasks}</div>
                {remoteStatus !== 'fresh' && <ProjectionState status={remoteStatus} />}
                <div style={styles.list}>
                  {pendingInteractions.map((interaction) => {
                    const run = runs.find((candidate) => candidate.id === interaction.runId)
                    return (
                      <button key={interaction.id} type="button" style={{ ...styles.card, ...styles.attentionCard }} onClick={() => answerInteraction(interaction)}>
                        <span style={styles.attentionIcon}>!</span>
                        <span style={styles.cardCopy}>
                          <strong style={styles.cardTitle}>{interaction.type === 'approval' ? 'Approval requested' : 'Agent question'}</strong>
                          <span style={styles.cardMeta}>{run?.title ?? interaction.runId}</span>
                          <span style={styles.cardDetail}>{interaction.prompt || interaction.toolName}</span>
                        </span>
                        <span style={styles.arrow}>Answer</span>
                      </button>
                    )
                  })}
                </div>

                <div style={styles.sectionTitle}>RECENT RUNS · {runs.length}</div>
                <div style={styles.list}>
                  {visibleRuns.map((run) => (
                    <button key={run.id} type="button" style={styles.card} onClick={() => inspectRun(run.id)}>
                      <span style={{ ...styles.statusDot, background: runStatusColor(run.status) }} />
                      <span style={styles.cardCopy}>
                        <strong style={styles.cardTitle}>{run.title}</strong>
                        <span style={styles.cardMeta}>{run.status.toUpperCase()}{run.currentPhase ? ` · ${run.currentPhase}` : ''}</span>
                      </span>
                      <span style={styles.arrow}>Inspect</span>
                    </button>
                  ))}
                  {runs.length === 0 && <EmptyState>No runs are visible yet.</EmptyState>}
                </div>

                <div ref={launchableTasksRef} data-testid="phone-launchable-tasks" tabIndex={-1} style={styles.sectionTitle}>
                  LAUNCHABLE TASKS · {tasks.length}
                </div>
                <div style={styles.list}>
                  {visibleTasks.map((task) => {
                    const launch = subjectLaunches[task.id]
                    const pending = launch?.status === 'pending' || (launch?.status === 'accepted' && !launch.observed)
                    return (
                      <div key={task.id} style={{ ...styles.card, cursor: 'default' }}>
                        <span style={{ ...styles.taskStatus, color: task.status === 'blocked' ? '#fda4af' : '#82d9f0' }}>{task.status.toUpperCase()}</span>
                        <span style={styles.cardCopy}>
                          <strong style={styles.cardTitle}>{task.title}</strong>
                          <span style={styles.cardMeta}>{task.id}{task.priority ? ` · ${task.priority}` : ''}</span>
                        </span>
                        {task.canLaunch && (
                          <button type="button" style={styles.inlineAction} disabled={pending} onClick={() => void launchTask(task.id)}>
                            {pending ? 'Syncing…' : 'Launch'}
                          </button>
                        )}
                      </div>
                    )
                  })}
                  {tasks.length === 0 && <EmptyState>No project tasks are visible.</EmptyState>}
                </div>
              </>
            ) : tab === 'people' ? (
              <>
                <div style={styles.sectionTitle}>ACTIVE TEAMMATES · {members.length}</div>
                {presenceStatus !== 'ready' && <ProjectionState status={presenceStatus} world />}
                <div style={styles.list}>
                  {members.map((member) => {
                    const destination = worldDestinationForRoom(member.roomId)
                    return (
                      <div key={member.playerId} style={styles.card}>
                        <span style={styles.humanAvatar}>◉</span>
                        <span style={styles.cardCopy}>
                          <strong style={styles.cardTitle}>{member.displayName}</strong>
                          <span style={styles.cardMeta}>HUMAN · {destination?.label ?? member.roomId}</span>
                          <span style={styles.cardDetail}>{member.pose.animation === 'walking' ? 'Moving now' : 'Active now'}</span>
                        </span>
                        {destination && (
                          <button type="button" style={styles.inlineAction} onClick={() => joinMemberRoom(member.roomId)}>
                            Join room
                          </button>
                        )}
                      </div>
                    )
                  })}
                  {members.length === 0 && <EmptyState>No other teammates are active in this authorized world.</EmptyState>}
                </div>
              </>
            ) : (
              <>
                <div style={styles.sectionTitle}>WORLD AND RUNTIME SYSTEMS</div>
                {remoteStatus !== 'fresh' && <ProjectionState status={remoteStatus} />}
                {presenceStatus !== 'ready' && <ProjectionState status={presenceStatus} world />}
                <div style={styles.systemGrid}>
                  <SystemMetric label="Presence" value={presenceStatus.toUpperCase()} healthy={presenceStatus === 'ready'} />
                  <SystemMetric label="World state" value={worldStateStatus.toUpperCase()} healthy={worldStateStatus === 'ready'} />
                  <SystemMetric label="Daemon" value={(health?.status ?? 'unavailable').toUpperCase()} healthy={health?.status === 'healthy'} />
                  <SystemMetric label="Agent capacity" value={health?.poolSize === undefined ? 'NOT PROJECTED' : `${health.activeAgents ?? 0} / ${health.poolSize}`} />
                  <SystemMetric label="Queue" value={health?.queueDepth === undefined ? 'NOT PROJECTED' : `${health.queueDepth} WAITING`} />
                  <SystemMetric label="Cost" value={health?.costSummary ? `$${health.costSummary.totalCostUsd.toFixed(2)}` : 'NOT PROJECTED'} />
                  <SystemMetric label="Version" value={health?.version || 'UNKNOWN'} />
                </div>
                <div style={styles.sectionTitle}>AUTHORIZED SERVICES · {health?.plugins?.length ?? 0}</div>
                <div style={styles.list}>
                  {(health?.plugins ?? []).map((plugin) => (
                    <div key={`${plugin.kind}:${plugin.name}`} style={{ ...styles.card, cursor: 'default' }}>
                      <span style={{ ...styles.statusDot, background: plugin.status === 'healthy' ? '#5eead4' : plugin.status === 'degraded' ? '#fbbf24' : '#fb7185' }} />
                      <span style={styles.cardCopy}>
                        <strong style={styles.cardTitle}>{plugin.name}</strong>
                        <span style={styles.cardMeta}>{plugin.kind.toUpperCase()} · {plugin.status.toUpperCase()}</span>
                      </span>
                    </div>
                  ))}
                  {!health?.plugins?.length && <EmptyState>No authorized service health is projected.</EmptyState>}
                </div>
              </>
            )}
            {notice && <div role="status" style={styles.notice}>{notice}</div>}
          </div>

          <div style={styles.privacy}>World navigation stays in Arena · Portal coordinates never exposed</div>
          <button type="button" style={styles.home} onClick={() => closePhone()} aria-label="Close phone" />
        </div>
      )}

      <button
        type="button"
        style={styles.launcher}
        onClick={openPhone}
        aria-label="Open workplace phone"
        aria-expanded={open}
      >
        <span style={styles.launcherGlyph}>▦</span>
        <span>PHONE</span>
        <kbd style={styles.keycap}>P</kbd>
      </button>
    </div>
  )
}

const styles: Record<string, CSSProperties> = {
  root: {
    position: 'fixed', left: '50%', bottom: 14, transform: 'translateX(-50%)',
    zIndex: 45, fontFamily: "'Nunito', system-ui, sans-serif", color: '#eef5ff',
    pointerEvents: 'none',
  },
  launcher: {
    pointerEvents: 'auto', display: 'flex', alignItems: 'center', gap: 8,
    margin: '0 auto', padding: '8px 12px', borderRadius: 999,
    border: '1px solid rgba(117, 232, 255, .38)', color: '#dff9ff',
    background: 'rgba(7, 18, 29, .86)', boxShadow: '0 8px 30px rgba(0,0,0,.35)',
    backdropFilter: 'blur(12px)', cursor: 'pointer', fontSize: 11,
    fontWeight: 800, letterSpacing: '.12em',
  },
  launcherGlyph: { color: '#6ee7f9', fontSize: 15 },
  keycap: {
    border: '1px solid rgba(255,255,255,.24)', borderRadius: 4, padding: '1px 5px',
    color: '#98aabd', background: 'rgba(255,255,255,.06)', font: '10px ui-monospace, monospace',
  },
  phone: {
    pointerEvents: 'auto', position: 'absolute', width: 'min(430px, calc(100vw - 28px))',
    height: 'min(650px, calc(100vh - 94px))', left: '50%', bottom: 48,
    transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column',
    padding: '16px 13px 13px', overflow: 'hidden', borderRadius: 30,
    border: '1px solid rgba(129, 224, 247, .36)', outline: 'none',
    background: 'linear-gradient(160deg, rgba(10,25,39,.98), rgba(5,10,18,.985))',
    boxShadow: '0 24px 80px rgba(0,0,0,.68), inset 0 0 0 3px rgba(255,255,255,.035)',
    backdropFilter: 'blur(20px)',
  },
  speaker: { width: 46, height: 4, borderRadius: 4, background: '#334559', margin: '0 auto 12px' },
  topline: { display: 'flex', justifyContent: 'space-between', fontSize: 10, fontWeight: 900, letterSpacing: '.14em', color: '#8ea5b9' },
  online: { color: '#73e2b7' },
  reconnecting: { color: '#d9b96d', fontSize: 8 },
  addressBar: { margin: '10px 0', padding: '9px 12px', borderRadius: 10, background: 'rgba(255,255,255,.055)', border: '1px solid rgba(255,255,255,.07)', color: '#b8c9d8', font: '11px ui-monospace, monospace' },
  lock: { color: '#6ee7f9', marginRight: 8 },
  tabs: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 5, marginBottom: 11 },
  tab: { position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '8px 4px', borderRadius: 8, borderWidth: 1, borderStyle: 'solid', borderColor: 'transparent', background: 'transparent', color: '#71869a', cursor: 'pointer', fontSize: 11, fontWeight: 800 },
  activeTab: { color: '#e7fbff', background: 'rgba(75, 202, 229, .13)', borderColor: 'rgba(91, 214, 239, .22)' },
  badge: { position: 'absolute', top: 2, right: 2, display: 'grid', placeItems: 'center', minWidth: 14, height: 14, padding: '0 3px', borderRadius: 999, background: '#ec6f7d', color: '#fff', fontSize: 7, fontWeight: 900 },
  viewport: { flex: 1, minHeight: 0, overflowY: 'auto', padding: '5px 2px' },
  sectionTitle: { margin: '2px 4px 9px', color: '#667f94', fontSize: 9, fontWeight: 900, letterSpacing: '.16em' },
  currentLocation: { display: 'flex', alignItems: 'center', gap: 6, margin: '0 4px 8px', color: '#9fb4c6', fontSize: 10, fontWeight: 800, letterSpacing: '.06em' },
  liveDot: { color: '#5eead4', fontSize: 8 },
  search: { boxSizing: 'border-box', width: '100%', margin: '0 0 9px', padding: '9px 11px', borderRadius: 10, border: '1px solid rgba(255,255,255,.09)', outline: 'none', background: 'rgba(255,255,255,.045)', color: '#eaf5ff', font: '11px inherit' },
  places: { display: 'grid', gap: 7 },
  list: { display: 'grid', gap: 7, marginBottom: 16 },
  systemGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 7, marginBottom: 16 },
  systemMetric: { display: 'flex', minWidth: 0, flexDirection: 'column', gap: 5, padding: '11px', borderRadius: 12, border: '1px solid rgba(255,255,255,.075)', background: 'rgba(255,255,255,.035)' },
  systemLabel: { color: '#71869a', fontSize: 9, fontWeight: 900, letterSpacing: '.12em', textTransform: 'uppercase' },
  systemValue: { overflow: 'hidden', textOverflow: 'ellipsis', color: '#dff9ff', font: '700 12px ui-monospace, monospace' },
  systemWarning: { color: '#fbbf24' },
  place: { display: 'flex', alignItems: 'center', width: '100%', gap: 10, padding: '10px 11px', textAlign: 'left', borderRadius: 12, border: '1px solid rgba(255,255,255,.075)', background: 'rgba(255,255,255,.035)', color: '#eaf5ff', cursor: 'pointer' },
  placeIcon: { display: 'grid', placeItems: 'center', flex: '0 0 32px', width: 32, height: 32, borderRadius: 10, background: 'linear-gradient(145deg, rgba(89,215,238,.2), rgba(122,104,245,.14))', color: '#7ce8f7', fontSize: 17 },
  placeCopy: { display: 'flex', flex: 1, minWidth: 0, flexDirection: 'column', gap: 2 },
  placeLabel: { fontSize: 12, letterSpacing: '.035em' },
  placeDescription: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#8195a8', fontSize: 10 },
  arrow: { color: '#5fcadd', fontSize: 11, fontWeight: 900 },
  card: { display: 'flex', alignItems: 'center', width: '100%', minWidth: 0, gap: 10, boxSizing: 'border-box', padding: '10px 11px', textAlign: 'left', borderRadius: 12, border: '1px solid rgba(255,255,255,.075)', background: 'rgba(255,255,255,.035)', color: '#eaf5ff', cursor: 'pointer' },
  attentionCard: { borderColor: 'rgba(251, 146, 60, .28)', background: 'rgba(251, 146, 60, .075)' },
  avatar: { display: 'grid', placeItems: 'center', flex: '0 0 32px', width: 32, height: 32, borderRadius: 10, background: 'rgba(139, 92, 246, .16)', color: '#b7a8ff', fontSize: 18 },
  humanAvatar: { display: 'grid', placeItems: 'center', flex: '0 0 32px', width: 32, height: 32, borderRadius: 999, background: 'rgba(52, 211, 153, .14)', color: '#6ee7b7', fontSize: 15 },
  attentionIcon: { display: 'grid', placeItems: 'center', flex: '0 0 28px', width: 28, height: 28, borderRadius: 999, background: 'rgba(251, 146, 60, .18)', color: '#fdba74', fontWeight: 900 },
  statusDot: { flex: '0 0 9px', width: 9, height: 9, borderRadius: 999 },
  taskStatus: { flex: '0 0 58px', fontSize: 8, fontWeight: 900, letterSpacing: '.08em' },
  cardCopy: { display: 'flex', flex: 1, minWidth: 0, flexDirection: 'column', gap: 2 },
  cardTitle: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, letterSpacing: '.02em' },
  cardMeta: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#879cb0', fontSize: 9, fontWeight: 800, letterSpacing: '.06em' },
  cardDetail: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#71869a', fontSize: 10 },
  inlineAction: { flex: '0 0 auto', padding: '6px 8px', borderRadius: 7, border: '1px solid rgba(95,202,221,.28)', background: 'rgba(65,172,194,.12)', color: '#8eeafa', cursor: 'pointer', fontSize: 9, fontWeight: 900 },
  empty: { padding: '22px 16px', textAlign: 'center', borderRadius: 12, border: '1px dashed rgba(255,255,255,.09)', color: '#71869a', fontSize: 11 },
  projectionState: { margin: '0 0 9px', padding: '8px 10px', borderRadius: 9, background: 'rgba(251,191,36,.08)', border: '1px solid rgba(251,191,36,.15)', color: '#d9b96d', fontSize: 10 },
  comingSoon: { height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 9, textAlign: 'center', color: '#7f93a7', fontSize: 12 },
  comingIcon: { color: '#6ee7f9', fontSize: 32 },
  notice: { marginTop: 8, padding: 9, borderRadius: 8, background: 'rgba(248,113,113,.12)', color: '#fecaca', fontSize: 11 },
  privacy: { padding: '9px 3px 7px', color: '#4f6679', fontSize: 9, textAlign: 'center' },
  home: { flex: '0 0 auto', width: 62, height: 5, margin: '1px auto 0', padding: 0, border: 0, borderRadius: 5, background: '#62768a', cursor: 'pointer' },
}
